/**
 * Advanced Monitor Detection Utility using Chrome's Window Management API
 * 
 * This utility implements multiple monitor detection methods to identify
 * external monitors and enforce single-display policies for proctoring.
 */

export interface ScreenInfo {
  id: string;
  width: number;
  height: number;
  availableArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  position: { x: number; y: number };
  scaleFactor: number;
  isPrimary: boolean;
  isInternal: boolean;
}

export interface MonitorDetectionResult {
  hasMultipleMonitors: boolean;
  screenCount: number;
  screens: ScreenInfo[];
  detectionMethod: string;
  confidence: 'low' | 'medium' | 'high';
  violations: string[];
  timestamp: string;
}

export class MonitorDetector {
  private hasPermission: boolean = false;
  private screenDetails: any = null;
  private fallbackMethods: boolean = true;

  constructor() {
    this.initialize();
  }

  /**
   * Initialize the monitor detector with feature detection
   */
  private async initialize(): Promise<boolean> {
    // Feature detection for Window Management API
    if (!('getScreenDetails' in window)) {
      console.warn('Window Management API not supported - using fallback methods');
      return false;
    }

    // Check permission status
    try {
      const permission = await navigator.permissions.query({ 
        name: 'window-management' as any
      });
      this.hasPermission = permission.state === 'granted';
      
      // Listen for permission changes
      permission.addEventListener('change', () => {
        this.hasPermission = permission.state === 'granted';
      });
    } catch (error) {
      console.error('Permission check failed:', error);
    }

    return true;
  }

  /**
   * Request permission for Window Management API
   */
  async requestPermission(): Promise<boolean> {
    if (!('getScreenDetails' in window)) {
      return false;
    }

    try {
      // This will trigger permission prompt if needed
      this.screenDetails = await (window as any).getScreenDetails();
      this.hasPermission = true;
      return true;
    } catch (error) {
      console.error('Permission request failed:', error);
      return false;
    }
  }

  /**
   * Detect monitors using Window Management API (primary method)
   */
  private async detectWithWindowManagementAPI(): Promise<MonitorDetectionResult | null> {
    if (!this.hasPermission || !this.screenDetails) {
      try {
        this.screenDetails = await (window as any).getScreenDetails();
      } catch (error) {
        return null;
      }
    }

    const screens: ScreenInfo[] = this.screenDetails.screens.map((screen: any) => ({
      id: screen.label || `screen_${screen.left}_${screen.top}`,
      width: screen.width,
      height: screen.height,
      availableArea: {
        x: screen.availLeft,
        y: screen.availTop,
        width: screen.availWidth,
        height: screen.availHeight
      },
      position: { x: screen.left, y: screen.top },
      scaleFactor: screen.devicePixelRatio,
      isPrimary: screen.isPrimary,
      isInternal: screen.isInternal
    }));

    const violations: string[] = [];
    const hasMultipleMonitors = screens.length > 1;

    if (hasMultipleMonitors) {
      violations.push(`Multiple displays detected: ${screens.length} screens`);
      
      const externalScreens = screens.filter(s => !s.isInternal);
      if (externalScreens.length > 0) {
        violations.push(`External monitors detected: ${externalScreens.length}`);
      }
    }

    return {
      hasMultipleMonitors,
      screenCount: screens.length,
      screens,
      detectionMethod: 'window_management_api',
      confidence: 'high',
      violations,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Safe window positioning analysis (no popup windows to prevent crashes)
   */
  private async detectWithWindowPositioning(): Promise<Partial<MonitorDetectionResult>> {
    const violations: string[] = [];
    let hasMultipleMonitors = false;

    try {
      // Current window position and dimensions
      const windowOuterWidth = window.outerWidth;
      const windowOuterHeight = window.outerHeight;
      const windowScreenX = window.screenX || window.screenLeft || 0;
      const windowScreenY = window.screenY || window.screenTop || 0;
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;

      // Window positioned beyond primary screen suggests multiple monitors
      if (windowScreenX < 0 || windowScreenX + windowOuterWidth > screenWidth ||
          windowScreenY < 0 || windowScreenY + windowOuterHeight > screenHeight) {
        hasMultipleMonitors = true;
        violations.push(`Window positioned beyond primary screen bounds (${windowScreenX}, ${windowScreenY})`);
      }

      // Check available screen space vs window space (safer method)
      const availableScreenRatio = (window.screen.availWidth * window.screen.availHeight) / (screenWidth * screenHeight);
      if (availableScreenRatio < 0.8) {
        violations.push('Significant screen space unavailable - possible multiple monitors with taskbars');
      }
    } catch (error) {
      console.warn('Window positioning detection failed:', error);
    }

    return {
      hasMultipleMonitors,
      violations,
      detectionMethod: 'safe_window_positioning',
      confidence: hasMultipleMonitors ? 'medium' : 'low'
    };
  }

  /**
   * Simplified screen analysis only (removed dangerous mouse tracking)
   */
  private async detectWithSafeAnalysis(): Promise<MonitorDetectionResult> {
    const screen = window.screen;
    const aspectRatio = screen.width / screen.height;
    const totalPixels = screen.width * screen.height;
    
    let estimatedCount = 1;
    let confidence: 'low' | 'medium' | 'high' = 'low';
    let detectionMethod = 'safe_screen_analysis';
    const violations: string[] = [];

    // Enhanced detection for common multi-monitor resolutions
    const commonMultiMonitorResolutions = [
      // Dual monitor setups
      { width: 3840, height: 1080, count: 2, desc: 'Dual 1920x1080' },
      { width: 2560, height: 1440, count: 2, desc: 'Dual 1280x1440' },
      { width: 3440, height: 1440, count: 2, desc: 'Ultrawide + monitor' },
      { width: 2560, height: 1080, count: 2, desc: 'Mixed resolution dual' },
      { width: 3360, height: 1050, count: 2, desc: 'Dual 1680x1050' },
      // Triple monitor setups
      { width: 5760, height: 1080, count: 3, desc: 'Triple 1920x1080' },
      { width: 3840, height: 1440, count: 3, desc: 'Triple 1280x1440' },
      { width: 5120, height: 1440, count: 3, desc: 'Triple mixed' },
      // Quad monitor setups
      { width: 7680, height: 1080, count: 4, desc: 'Quad 1920x1080' },
      { width: 3840, height: 2160, count: 4, desc: 'Quad 1920x1080 (2x2)' },
    ];

    // Check exact resolution matches
    for (const config of commonMultiMonitorResolutions) {
      if (screen.width === config.width && screen.height === config.height) {
        estimatedCount = config.count;
        confidence = 'high';
        detectionMethod = 'exact_resolution_match';
        violations.push(`${config.desc} configuration detected (${config.width}x${config.height})`);
        break;
      }
    }

    // If no exact match, use heuristic analysis
    if (estimatedCount === 1) {
      // Ultra-wide monitors (likely multiple displays)
      if (screen.width >= 3440 || aspectRatio >= 2.4) {
        estimatedCount = Math.ceil(screen.width / 1920);
        confidence = aspectRatio >= 3.0 ? 'high' : 'medium';
        detectionMethod = 'ultrawide_analysis';
        violations.push(`Ultra-wide display detected: ${screen.width}x${screen.height} (aspect ratio: ${aspectRatio.toFixed(2)})`);
      }
      // Very high pixel count suggests multiple monitors
      else if (totalPixels > 4000000) {
        estimatedCount = Math.ceil(totalPixels / 2073600);
        confidence = 'medium';
        detectionMethod = 'high_pixel_count';
        violations.push(`High pixel count detected: ${totalPixels} pixels suggests multiple displays`);
      }
    }

    // Enhanced screen property checks
    if ('isExtended' in screen && (screen as any).isExtended) {
      estimatedCount = Math.max(estimatedCount, 2);
      confidence = 'high';
      detectionMethod += '_extended_display';
      violations.push('Extended display configuration detected');
    }

    const primaryScreen: ScreenInfo = {
      id: 'primary',
      width: screen.width,
      height: screen.height,
      availableArea: {
        x: (screen as any).availLeft || 0,
        y: (screen as any).availTop || 0,
        width: screen.availWidth,
        height: screen.availHeight
      },
      position: { x: 0, y: 0 },
      scaleFactor: window.devicePixelRatio || 1,
      isPrimary: true,
      isInternal: true
    };

    return {
      hasMultipleMonitors: estimatedCount > 1,
      screenCount: estimatedCount,
      screens: [primaryScreen],
      detectionMethod,
      confidence,
      violations,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Safe monitor detection using only reliable methods (prevents browser crashes)
   */
  async detectMonitors(): Promise<MonitorDetectionResult> {
    try {
      // Try Window Management API first (most accurate and safe)
      const apiResult = await this.detectWithWindowManagementAPI();
      if (apiResult) {
        return apiResult;
      }

      // Use only safe fallback methods
      const screenAnalysis = await this.detectWithSafeAnalysis();
      const windowPositioning = await this.detectWithWindowPositioning();

      // Combine safe results
      const combinedViolations = [
        ...screenAnalysis.violations,
        ...(windowPositioning.violations || [])
      ];

      const hasMultipleMonitors = 
        screenAnalysis.hasMultipleMonitors ||
        windowPositioning.hasMultipleMonitors;

      return {
        hasMultipleMonitors,
        screenCount: screenAnalysis.screenCount,
        screens: screenAnalysis.screens,
        detectionMethod: `${screenAnalysis.detectionMethod}_${windowPositioning.detectionMethod || 'safe'}`,
        confidence: hasMultipleMonitors ? (screenAnalysis.confidence === 'high' ? 'high' : 'medium') : 'low',
        violations: combinedViolations,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Monitor detection failed:', error);
      
      // Return safe fallback result
      return {
        hasMultipleMonitors: false,
        screenCount: 1,
        screens: [{
          id: 'primary',
          width: window.screen.width,
          height: window.screen.height,
          availableArea: {
            x: 0,
            y: 0,
            width: window.screen.availWidth,
            height: window.screen.availHeight
          },
          position: { x: 0, y: 0 },
          scaleFactor: window.devicePixelRatio || 1,
          isPrimary: true,
          isInternal: true
        }],
        detectionMethod: 'error_fallback',
        confidence: 'low',
        violations: [`Detection error: ${error}`],
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Safe continuous monitoring (prevents duplicate event listeners and crashes)
   */
  startContinuousMonitoring(
    callback: (result: MonitorDetectionResult) => void,
    intervalMs: number = 3000
  ): () => void {
    let isActive = true;
    let detectionInProgress = false;

    const performDetection = async () => {
      if (!isActive || detectionInProgress) return;
      
      detectionInProgress = true;
      try {
        const result = await this.detectMonitors();
        if (isActive) {
          callback(result);
        }
      } catch (error) {
        console.error('Detection error:', error);
      } finally {
        detectionInProgress = false;
      }
    };

    // Initial detection
    performDetection();

    // Set up periodic checks with safety guards
    const interval = setInterval(() => {
      if (isActive) {
        performDetection();
      }
    }, intervalMs);

    // Return cleanup function
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }

  /**
   * Check if the current configuration should be blocked
   */
  shouldBlockAccess(result: MonitorDetectionResult, strictMode: boolean = true): boolean {
    if (!strictMode) {
      // Only block if we have high confidence in multiple monitor detection
      return result.hasMultipleMonitors && result.confidence === 'high';
    }
    
    // Strict mode: block any indication of multiple monitors
    return result.hasMultipleMonitors;
  }

  /**
   * Generate detailed violation report
   */
  generateViolationReport(result: MonitorDetectionResult): string {
    if (!result.hasMultipleMonitors) {
      return 'No monitor violations detected.';
    }

    const report = [
      `Monitor Violation Report - ${result.timestamp}`,
      `Detection Method: ${result.detectionMethod}`,
      `Confidence Level: ${result.confidence}`,
      `Screen Count: ${result.screenCount}`,
      '',
      'Violations Detected:'
    ];

    result.violations.forEach((violation, index) => {
      report.push(`${index + 1}. ${violation}`);
    });

    if (result.screens.length > 1) {
      report.push('', 'Screen Details:');
      result.screens.forEach((screen, index) => {
        report.push(
          `Screen ${index + 1}: ${screen.width}x${screen.height} at (${screen.position.x}, ${screen.position.y})${screen.isPrimary ? ' [PRIMARY]' : ''}${screen.isInternal ? ' [INTERNAL]' : ' [EXTERNAL]'}`
        );
      });
    }

    return report.join('\n');
  }
}

// Singleton instance for global use
export const monitorDetector = new MonitorDetector();

// Utility functions for easy integration
export async function checkForMultipleMonitors(): Promise<MonitorDetectionResult> {
  return await monitorDetector.detectMonitors();
}

export async function requestMonitorPermissions(): Promise<boolean> {
  return await monitorDetector.requestPermission();
}

export function startMonitorMonitoring(
  callback: (result: MonitorDetectionResult) => void,
  intervalMs?: number
): () => void {
  return monitorDetector.startContinuousMonitoring(callback, intervalMs);
}